import React, {useEffect, useState} from "react";
import {Form, Row, Col, Button} from "react-bootstrap";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "./SearchbarSingleRoute.css";
import BookingModal from "./BookingModal";
import routesData, {ferryRoutesGeoJSON} from "./routesDb";

const SearchbarSingleRoute = (props) => {
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedReturnDate, setSelectedReturnDate] = useState(null);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Heutiges Datum um Mitternacht (ohne Zeit)
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Datum in 2 Monaten
  const twoMonthsFromNow = new Date();
  twoMonthsFromNow.setMonth(twoMonthsFromNow.getMonth() + 2);

  const handleDateChange = (date) => {
    setSelectedDate(date);
    // Wenn Rückfahrtdatum vor Hinfahrtdatum liegt, zurücksetzen
    if (selectedReturnDate && date && selectedReturnDate < date) {
      setSelectedReturnDate(null);
    }
  };

  const handleReturnDateChange = (date) => {
    setSelectedReturnDate(date);
  };

  const handleBookingClick = () => {
    setShowBookingModal(true);
  };

  const handleCloseBookingModal = () => {
    setShowBookingModal(false);
  };

  // Berechne Mindestdatum für Rückfahrt (Tag nach Hinfahrt)
  const minReturnDate = selectedDate
    ? new Date(selectedDate.getTime() + 24 * 60 * 60 * 1000)
    : today;

  const coordinatesMatch = (lng1, lat1, lng2, lat2) => {
    return Math.abs(lng1 - lng2) < 0.0001 && Math.abs(lat1 - lat2) < 0.0001;
  };

  const getConnectedPorts = (selectedLoadingPort) => {
    if (!selectedLoadingPort) return [];

    const connectedPorts = [];

    ferryRoutesGeoJSON.features.forEach((route) => {
      const coordinates = route.geometry.coordinates[0];
      const firstPoint = coordinates[0];
      const lastPoint = coordinates[coordinates.length - 1];

      const isStartPort = coordinatesMatch(
        firstPoint[0],
        firstPoint[1],
        selectedLoadingPort.lng,
        selectedLoadingPort.lat,
      );

      const isEndPort = coordinatesMatch(
        lastPoint[0],
        lastPoint[1],
        selectedLoadingPort.lng,
        selectedLoadingPort.lat,
      );

      if (isStartPort) {
        const destinationPort = routesData.find((port) =>
          coordinatesMatch(port.lng, port.lat, lastPoint[0], lastPoint[1]),
        );

        if (
          destinationPort &&
          !connectedPorts.some((port) =>
            coordinatesMatch(
              port.lng,
              port.lat,
              destinationPort.lng,
              destinationPort.lat,
            ),
          )
        ) {
          connectedPorts.push(destinationPort);
        }
      }

      if (isEndPort) {
        const startPort = routesData.find((port) =>
          coordinatesMatch(port.lng, port.lat, firstPoint[0], firstPoint[1]),
        );

        if (
          startPort &&
          !connectedPorts.some((port) =>
            coordinatesMatch(port.lng, port.lat, startPort.lng, startPort.lat),
          )
        ) {
          connectedPorts.push(startPort);
        }
      }
    });

    return connectedPorts;
  };

  const availableDestinationPorts = getConnectedPorts(
    props.selectedPortofLoading,
  );

  const sortPortsAscending = (ports) => {
    return [...ports].sort((firstPort, secondPort) => {
      const countryCompare = firstPort.country.localeCompare(
        secondPort.country,
        "de",
        {sensitivity: "base"},
      );

      if (countryCompare !== 0) {
        return countryCompare;
      }

      return firstPort.name.localeCompare(secondPort.name, "de", {
        sensitivity: "base",
      });
    });
  };

  const sortedLoadingPorts = sortPortsAscending(routesData);
  const sortedDestinationPorts = sortPortsAscending(availableDestinationPorts);

  const getPortValue = (port) => {
    return `${port.country}|${port.name}|${port.lat}|${port.lng}`;
  };

  return (
    <div className="searchbar-single-route">
      <Form>
        <Row>
          <Col xs={12} md={props.isRoundTrip ? 3 : 4}>
            <Form.Group controlId="formBasicEmail">
              {isMobile ? (
                <Form.Select
                  value={
                    props.selectedPortofLoading
                      ? getPortValue(props.selectedPortofLoading)
                      : ""
                  }
                  onChange={(event) => {
                    const selectedPort = routesData.find(
                      (port) => getPortValue(port) === event.target.value,
                    );

                    props.handleSelectLoadingPort(selectedPort || null);
                  }}
                >
                  <option value="">Starthafen wählen...</option>
                  {sortedLoadingPorts.map((port) => (
                    <option key={getPortValue(port)} value={getPortValue(port)}>
                      {`${port.country}: ${port.name}`}
                    </option>
                  ))}
                </Form.Select>
              ) : (
                <Form.Control
                  type="text"
                  placeholder="Starthafen wählen..."
                  onClick={(event) => {
                    props.handleItemClick(event);
                    props.handleShowModalSelectPort("loading");
                  }}
                  value={
                    props.selectedPortofLoading &&
                    props.selectedPortofLoading.name
                      ? props.selectedPortofLoading.name
                      : ""
                  }
                  readOnly
                />
              )}
            </Form.Group>
          </Col>

          <Col xs={12} md={props.isRoundTrip ? 3 : 4}>
            <Form.Group controlId="formBasicPassword">
              {isMobile ? (
                <Form.Select
                  value={
                    props.selectedPortofDischarge
                      ? getPortValue(props.selectedPortofDischarge)
                      : ""
                  }
                  disabled={
                    !props.selectedPortofLoading ||
                    !props.selectedPortofLoading.name
                  }
                  onChange={(event) => {
                    const selectedPort = availableDestinationPorts.find(
                      (port) => getPortValue(port) === event.target.value,
                    );

                    props.handleSelectDischargePort(selectedPort || null);
                  }}
                >
                  <option value="">Zielhafen wählen...</option>
                  {sortedDestinationPorts.map((port) => (
                    <option key={getPortValue(port)} value={getPortValue(port)}>
                      {`${port.country}: ${port.name}`}
                    </option>
                  ))}
                </Form.Select>
              ) : (
                <Form.Control
                  type="text"
                  placeholder="Zielhafen wählen..."
                  disabled={
                    props.selectedPortofLoading &&
                    props.selectedPortofLoading.name
                      ? false
                      : true
                  }
                  onClick={(event) => {
                    props.handleItemClick(event);
                    props.handleShowModalSelectPort("discharge");
                  }}
                  value={
                    props.selectedPortofDischarge &&
                    props.selectedPortofDischarge.name
                      ? props.selectedPortofDischarge.name
                      : ""
                  }
                  readOnly
                />
              )}
            </Form.Group>
          </Col>

          <Col xs={12} md={2}>
            <Form.Group controlId="formBasicDate">
              <DatePicker
                selected={selectedDate}
                onChange={handleDateChange}
                minDate={today}
                maxDate={twoMonthsFromNow}
                monthsShown={isMobile ? 1 : 2}
                dateFormat="dd.MM.yyyy"
                placeholderText="Hinfahrt: TT.MM.JJJJ"
                className="form-control"
                disabled={
                  props.selectedPortofDischarge &&
                  props.selectedPortofDischarge.name
                    ? false
                    : true
                }
                showPopperArrow={false}
                calendarStartDay={1}
                popperPlacement={isMobile ? "bottom-start" : "top-start"}
              />
            </Form.Group>
          </Col>

          {props.isRoundTrip && (
            <Col xs={12} md={2}>
              <Form.Group controlId="formBasicReturnDate">
                <DatePicker
                  selected={selectedReturnDate}
                  onChange={handleReturnDateChange}
                  minDate={minReturnDate}
                  maxDate={twoMonthsFromNow}
                  monthsShown={isMobile ? 1 : 2}
                  dateFormat="dd.MM.yyyy"
                  placeholderText="Rückfahrt: TT.MM.JJJJ"
                  className="form-control"
                  disabled={!selectedDate}
                  showPopperArrow={false}
                  calendarStartDay={1}
                  popperPlacement={isMobile ? "bottom-start" : "top-start"}
                />
              </Form.Group>
            </Col>
          )}

          <Col xs={12} md={2}>
            <Button
              variant="success"
              style={{width: "100%"}}
              disabled={
                props.isRoundTrip
                  ? !selectedDate || !selectedReturnDate
                  : !selectedDate
              }
              onClick={handleBookingClick}
            >
              Buchen
            </Button>
          </Col>
        </Row>
      </Form>

      <BookingModal
        show={showBookingModal}
        onHide={handleCloseBookingModal}
        selectedPortofLoading={props.selectedPortofLoading}
        selectedPortofDischarge={props.selectedPortofDischarge}
        selectedDate={selectedDate}
        selectedReturnDate={selectedReturnDate}
        isRoundTrip={props.isRoundTrip}
      />
    </div>
  );
};

export default SearchbarSingleRoute;
